import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de temps: addition et soustraction";
const DESCRIPTION =
  "Additionnez ou soustrayez des durees en jours, heures, minutes et secondes. Outil pratique pour horaires de travail, trajets, planning de projet et organisation quotidienne.";
const KEYWORDS =
  "calculatrice de temps, addition heures, soustraction duree, calcul heures minutes secondes, duree de travail";
const OG_URL = "https://do-calculate.com/calculator/fr/time-calculator/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de Temps",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice de Temps",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Comment utiliser la calculatrice de temps?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Entrez le temps initial en jours, heures, minutes et secondes, sélectionnez l'opération (addition ou soustraction), entrez le deuxième temps, puis cliquez sur le bouton Calculer pour obtenir le résultat.",
        },
      },
      {
        "@type": "Question",
        name: "À quoi sert une calculatrice de temps?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Elle est utilisée pour calculer les heures de travail, la durée totale des projets, les temps de trajet, la planification des horaires, le calcul des heures supplémentaires, et toute opération mathématique liée à la durée.",
        },
      },
    ],
  },
];

// Helper component for time input groups
const TimeInputGroup = ({ day, hour, min, sec, setDay, setHour, setMin, setSec }) => (
  <div className="d-flex flex-wrap gap-2 justify-content-center">
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input
        type="number"
        className="form-control"
        id="day"
        placeholder="Jours"
        value={day}
        onChange={(e) => setDay(e.target.value)}
        min="0"
      />
      <label htmlFor="day">Jours</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input
        type="number"
        className="form-control"
        id="hour"
        placeholder="Heures"
        value={hour}
        onChange={(e) => setHour(e.target.value)}
        min="0"
        max="23"
      />
      <label htmlFor="hour">Heures</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input
        type="number"
        className="form-control"
        id="min"
        placeholder="Minutes"
        value={min}
        onChange={(e) => setMin(e.target.value)}
        min="0"
        max="59"
      />
      <label htmlFor="min">Minutes</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input
        type="number"
        className="form-control"
        id="sec"
        placeholder="Secondes"
        value={sec}
        onChange={(e) => setSec(e.target.value)}
        min="0"
        max="59"
      />
      <label htmlFor="sec">Secondes</label>
    </div>
  </div>
);

function TimeCalculator() {
  const [time1Day, setTime1Day] = useState("0");
  const [time1Hour, setTime1Hour] = useState("5");
  const [time1Min, setTime1Min] = useState("30");
  const [time1Sec, setTime1Sec] = useState("0");

  const [operation, setOperation] = useState("add");

  const [time2Day, setTime2Day] = useState("0");
  const [time2Hour, setTime2Hour] = useState("3");
  const [time2Min, setTime2Min] = useState("15");
  const [time2Sec, setTime2Sec] = useState("0");

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const calculateTime = () => {
    const t1 = {
      totalSeconds:
        parseInt(time1Day) * 86400 +
        parseInt(time1Hour) * 3600 +
        parseInt(time1Min) * 60 +
        parseInt(time1Sec),
    };

    const t2 = {
      totalSeconds:
        parseInt(time2Day) * 86400 +
        parseInt(time2Hour) * 3600 +
        parseInt(time2Min) * 60 +
        parseInt(time2Sec),
    };

    let resultSeconds;
    if (operation === "add") {
      resultSeconds = t1.totalSeconds + t2.totalSeconds;
    } else {
      resultSeconds = t1.totalSeconds - t2.totalSeconds;
    }

    if (resultSeconds < 0 && operation === "subtract") {
      setError("Le résultat ne peut pas être négatif");
      setResult(null);
      return;
    }

    const days = Math.floor(resultSeconds / 86400);
    const hours = Math.floor((resultSeconds % 86400) / 3600);
    const minutes = Math.floor((resultSeconds % 3600) / 60);
    const seconds = resultSeconds % 60;

    setResult({
      days,
      hours,
      minutes,
      seconds,
      totalSeconds: resultSeconds,
      formatted: `${days}j ${hours}h ${minutes}m ${seconds}s`,
    });
    setError(null);
  };

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center p-3">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>

          <div className="border border-light p-3">
            <p>
              Cette calculatrice de temps gratuite vous aide à ajouter ou soustraire des durées facilement. Vous pouvez calculer des valeurs en jours, heures, minutes et secondes. C'est idéal pour calculer les heures de travail, les durées de projet, et bien plus.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle p-3 bg-light rounded">
              <div className="p-2 mb-3 bg-success text-white">
                <strong>Première Durée</strong>
              </div>
              <TimeInputGroup
                day={time1Day}
                hour={time1Hour}
                min={time1Min}
                sec={time1Sec}
                setDay={setTime1Day}
                setHour={setTime1Hour}
                setMin={setTime1Min}
                setSec={setTime1Sec}
              />

              <div className="mb-3 mt-4">
                <label><strong>Opération:</strong></label>
                <div className="btn-group d-flex" role="group">
                  <button
                    type="button"
                    className={`btn flex-fill ${operation === "add" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setOperation("add")}
                  >
                    Ajouter (+)
                  </button>
                  <button
                    type="button"
                    className={`btn flex-fill ${operation === "subtract" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setOperation("subtract")}
                  >
                    Soustraire (-)
                  </button>
                </div>
              </div>

              <div className="p-2 mb-3 bg-success text-white">
                <strong>Deuxième Durée</strong>
              </div>
              <TimeInputGroup
                day={time2Day}
                hour={time2Hour}
                min={time2Min}
                sec={time2Sec}
                setDay={setTime2Day}
                setHour={setTime2Hour}
                setMin={setTime2Min}
                setSec={setTime2Sec}
              />

              <div className="text-center mt-4">
                <button className="btn btn-success btn-lg px-5" onClick={calculateTime}>
                  Calculer
                </button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {result && (
                <div className="alert alert-success mt-4">
                  <h3>Résultat:</h3>
                  <div style={{ fontSize: "28px", color: "#28a745", fontWeight: "bold", marginBottom: "10px" }}>
                    {result.formatted}
                  </div>
                  <div className="mt-3">
                    <p><strong>Détails:</strong></p>
                    <ul>
                      <li>Jours: {result.days}</li>
                      <li>Heures: {result.hours}</li>
                      <li>Minutes: {result.minutes}</li>
                      <li>Secondes: {result.seconds}</li>
                      <li>Secondes totales: {result.totalSeconds}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Calculatrice de temps</h3></div>
              <div className="info-section-body">
                <p>Cette calculatrice additionne ou soustrait des durees en heures, minutes et secondes. Elle sert a verifier un total d'heures travaillees, calculer des temps de trajet, estimer une duree de projet ou convertir une valeur decimale en format horaire lisible.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conversions de temps utiles</h3></div>
              <div className="info-section-body">
                <ul>
                  <li>1 heure = 60 minutes = 3 600 secondes</li>
                  <li>1 jour = 24 heures = 1 440 minutes = 86 400 secondes</li>
                  <li>1 semaine = 7 jours = 168 heures</li>
                  <li>1 mois moyen = 30,44 jours = 730,5 heures</li>
                  <li>1 an civil moyen = 365,25 jours = 8 766 heures</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Exemples pratiques d'utilisation</div>
              <div className="info-section-body">
                <p><strong>Exemple 1 — Temps de travail restant :</strong></p>
                <div className="alert alert-info" style={{ lineHeight: "1.8" }}>Durée totale 5 h, durée effectuée 2 h → Restant = <strong>3 h</strong></div>
                <p><strong>Exemple 2 — Addition de temps de trajet :</strong></p>
                <div className="alert alert-info" style={{ lineHeight: "1.8" }}>5 h 30 + 2 h 15 + 3 h 50 = <strong>11 h 35</strong></div>
                <p><strong>Exemple 3 — Heures restantes sur un projet :</strong></p>
                <div className="alert alert-info" style={{ lineHeight: "1.8" }}>Budget 40 h, consommé 28 h 30 → Restant = <strong>11 h 30</strong></div>
                <p><strong>Exemple 4 — Total d'entraînement sportif :</strong></p>
                <div className="alert alert-info" style={{ lineHeight: "1.8" }}>15 min + 45 min + 20 min + 10 min = <strong>1 h 30</strong></div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Conseils pour bien utiliser la calculatrice de temps</div>
              <div className="info-section-body">
                <ul>
                  <li>Utilisez l'unité adaptée : jours pour de longs projets, heures pour le travail quotidien</li>
                  <li>Les champs vides sont traités comme zéro — pas de risque d'erreur</li>
                  <li>Attention : 60 minutes = 1 heure (pas 100) et 60 secondes = 1 minute</li>
                  <li>Pour une soustraction, assurez-vous que la première durée est supérieure à la seconde</li>
                  <li>Pour un usage professionnel, notez le résultat dans une feuille de calcul ou une copie d'écran</li>
                  <li>L'outil fonctionne hors connexion après le premier chargement de la page</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Faits intéressants sur le temps</div>
              <div className="info-section-body">
                <ul>
                  <li>Une horloge atomique dévie de <strong>±1 seconde sur 100 millions d'années</strong></li>
                  <li>Des « secondes intercalaires » sont ajoutées périodiquement pour synchroniser les horloges mondiales avec la rotation terrestre</li>
                  <li>Selon la relativité d'Einstein, le temps s'écoule légèrement plus lentement à haute vitesse ou près d'une forte gravité</li>
                  <li>1 an civil = <strong>31 536 000 secondes</strong></li>
                  <li>1 journée = <strong>86 400 secondes</strong></li>
                  <li>Sur 80 ans de vie, on dort environ <strong>229 961 heures</strong> (soit près de 26 ans)</li>
                  <li>Les Babyloniens ont inventé la division du temps en 60 minutes et 60 secondes (système sexagésimal)</li>
                  <li>L'Égypte antique est la première à avoir divisé la journée en 24 heures</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Comment calculer des heures de travail sur plusieurs jours ?</strong></h3>
                <p>Entrez la durée de chaque journée (ex : 8 h 30, 7 h 45, 9 h 00) et additionnez-les. La calculatrice convertit automatiquement le surplus de minutes en heures.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Pourquoi 60 minutes dans une heure et pas 100 ?</strong></h3>
                <p>Le système sexagésimal (base 60) vient des Babyloniens (~2000 av. J.-C.). 60 est divisible par 1, 2, 3, 4, 5, 6, 10, 12, 15, 20 et 30 — ce qui facilitait les calculs sans fractions.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Que faire si le résultat est négatif ?</strong></h3>
                <p>Un résultat négatif signifie que la seconde durée est supérieure à la première. Vérifiez l'ordre de vos valeurs ou inversez-les.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Comment convertir des heures décimales en h:min ?</strong></h3>
                <p>Multipliez la partie décimale par 60. Exemple : 2,75 heures = 2 h + (0,75 × 60) = <strong>2 h 45 min</strong>.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimeCalculator;
